import type { PrefaceQuestion, SurveyConfig } from './types';
import { useRef, useState } from 'react';
import { createFeishuTable, testFeishuWebhook } from '../../lib/api';

type Props = {
  config: SurveyConfig;
  onChange: (patch: Partial<SurveyConfig>) => void;
  onClose: () => void;
};

/**
 * 设置抽屉（右侧 slide-out，截图 1 风格）
 *  - 基础 / 签收 / 电子签 / 改答 / AI / 提交结果 几组
 *  - 开关用 <Switch> 组件
 *  - "谁可以填写" 用 <select>
 *  - 每组开关联动后展示子配置（time picker / 富文本 / mode select / 数字输入）
 */
export default function SettingsPanel({ config, onChange, onClose }: Props) {
  const s = config.settings ?? {};
  const update = (patch: Partial<typeof s>) => onChange({ settings: { ...s, ...patch } });

  // 前置页：标题/正文/图片 + 独立填写项（随协议页一起填写，来自前端同学 0831 版本）
  const preface = s.preface ?? {};
  const prefaceQuestions = preface.questions ?? [];
  const updatePreface = (patch: Partial<typeof preface>) => update({ preface: { ...preface, ...patch } });
  const addPrefaceQuestion = (type: PrefaceQuestion['type']) => {
    const usedIds = new Set(prefaceQuestions.map((question) => question.id));
    let number = prefaceQuestions.length + 1;
    while (usedIds.has(`P${number}`)) number += 1;
    const id = `P${number}`;
    const question: PrefaceQuestion = {
      id,
      type,
      title: type === 'text' ? '请填写' : '请补充说明',
      required: true,
      ...(type === 'longtext' ? { params: { maxLen: 500 } } : {}),
    };
    updatePreface({ questions: [...prefaceQuestions, question] });
  };
  const updatePrefaceQuestion = (index: number, patch: Partial<PrefaceQuestion>) => {
    updatePreface({ questions: prefaceQuestions.map((question, i) => i === index ? { ...question, ...patch } : question) });
  };
  const removePrefaceQuestion = (index: number) => updatePreface({ questions: prefaceQuestions.filter((_, i) => i !== index) });

  const [feishuTesting, setFeishuTesting] = useState(false);
  const [feishuMsg, setFeishuMsg] = useState<string | null>(null);
  const [tableBusy, setTableBusy] = useState(false);
  const [tableMsg, setTableMsg] = useState<string | null>(null);

  // 直连多维表格：服务端建表并绑定；把绑定同步进本地草稿，避免下次保存覆盖掉
  const handleCreateTable = async () => {
    setTableBusy(true);
    setTableMsg(null);
    try {
      const r: any = await createFeishuTable(config.id);
      if (r.binding) update({ feishuTable: r.binding } as any);
      setTableMsg(r.existing ? '本问卷已关联多维表格' : '✓ 已创建并关联');
    } catch (e: any) {
      const err = e?.body;
      setTableMsg(
        err?.error === 'feishu_not_configured'
          ? '✗ 服务器未配置飞书应用凭据（需 FEISHU_APP_ID / FEISHU_APP_SECRET，可选 FEISHU_WIKI_SPACE_ID 建到知识库）'
          : err?.error === 'not_found'
            ? '✗ 请先点顶栏「保存」再创建'
            : `✗ 创建失败：${err?.detail ?? err?.error ?? e?.message ?? '未知错误'}`,
      );
    } finally {
      setTableBusy(false);
    }
  };

  const handleFeishuTest = async () => {
    if (!config.id) return;
    setFeishuTesting(true);
    setFeishuMsg(null);
    try {
      // 带上抽屉里的草稿值：还没点「保存」也能直接测
      const r = await testFeishuWebhook(config.id, {
        webhookUrl: (s.feishu as any)?.webhookUrl,
        bearerToken: (s.feishu as any)?.bearerToken,
      });
      if (r.ok) {
        setFeishuMsg('✓ 已发送测试包，去飞书自动化看是否收到');
      } else {
        const detail = r.body ? ` — ${r.body}` : '';
        setFeishuMsg(`✗ 失败：${r.error}${detail}`);
      }
    } catch (e: any) {
      setFeishuMsg(`✗ 失败：${e?.message || '网络错误'}`);
    } finally {
      setFeishuTesting(false);
    }
  };

  return (
    <>
      <div className="drawer-overlay" onClick={onClose} />
      <aside className="drawer" role="dialog" aria-label="设置">
        <header className="drawer-head">
          <div className="drawer-title">
            <IconGear />
            <span>设置</span>
          </div>
          <button className="drawer-close" onClick={onClose} aria-label="关闭">
            <IconX />
          </button>
        </header>

        <div className="drawer-body">
          {/* 基础 */}
          <div className="drawer-section">
            <div className="drawer-section-label">基础</div>

            <SettingRow label="停止收集">
              <Switch checked={!!s.stopCollect} onChange={(v) => update({ stopCollect: v })} />
            </SettingRow>

            <SettingRow label="匿名填写">
              <Switch checked={!!s.anonymous} onChange={(v) => update({ anonymous: v })} />
            </SettingRow>

            <div className="setting-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6 }}>
              <div className="setting-label">谁可以填写</div>
              <div className="setting-control">
                <div className="select">
                  <select
                    value={(s.whoCanFill as string | undefined) ?? 'anyone'}
                    onChange={(e) => update({ whoCanFill: e.target.value })}
                  >
                    <option value="anyone">任何人都可以填写</option>
                    <option value="login">仅登录用户</option>
                    <option value="invite">仅凭邀请链接</option>
                  </select>
                </div>
              </div>
              {s.whoCanFill === 'invite' && (
                <>
                  <input
                    className="input"
                    value={(s as any).inviteToken ?? ''}
                    onChange={(e) => update({ inviteToken: e.target.value } as any)}
                    placeholder="邀请令牌（选填；填写链接需带 ?invite=令牌）"
                  />
                  <div className="hint" style={{ padding: '0 4px' }}>
                    分享链接：/s/{config.id}?invite=令牌。设置了令牌则校验一致；留空只要求链接带 invite 参数。
                  </div>
                </>
              )}
              {s.whoCanFill === 'login' && (
                <div className="hint" style={{ padding: '0 4px' }}>
                  本期无登录态，"仅登录"暂按公开处理，建议先用"仅凭邀请链接"。
                </div>
              )}
            </div>

            <SettingRow label="填写需登录验证">
              <Switch
                checked={!!s.loginRequired}
                onChange={(v) => update({ loginRequired: v })}
              />
            </SettingRow>

            <SettingRow label="每人限填一次">
              <Switch
                checked={!!(s as any).oncePerUser}
                onChange={(v) => update({ oncePerUser: v } as any)}
              />
            </SettingRow>
            {Boolean((s as any).oncePerUser) && (
              <div className="hint" style={{ padding: '0 4px' }}>
                按浏览器设备识别，同一设备只能提交一次（防误填重复；清除浏览器数据后可再填）。
                需要改答请同时开启下方「专属链接改答」。
              </div>
            )}
          </div>

          {/* 签收前置阅读页 */}
          <div className="drawer-section">
            <div className="drawer-section-label">签收前置</div>

            <SettingRow label="启用前置阅读页">
              <Switch
                checked={!!s.preface?.requireCheck}
                onChange={(v) =>
                  update({ preface: { ...(s.preface ?? {}), requireCheck: v } })
                }
              />
            </SettingRow>

            {s.preface?.requireCheck && (
              <div className="drawer-sub-fields preface-settings-fields">
                <input
                  className="input"
                  value={preface.title ?? ''}
                  onChange={(e) => updatePreface({ title: e.target.value })}
                  placeholder="前置标题（如：签收须知）"
                />
                <textarea
                  className="textarea"
                  rows={4}
                  value={preface.body ?? ''}
                  onChange={(e) => updatePreface({ body: e.target.value })}
                  placeholder="前置正文（支持换行）"
                />
                <PrefaceImageField image={preface.image} onChange={(image) => updatePreface({ image })} />
                <div className="preface-question-head">
                  <span className="qe-label">前置填写项</span>
                  <span className="hint">在阅读确认前补充收集信息（题目卡片上的「前置页」标记也会显示在前置页）</span>
                </div>
                {prefaceQuestions.map((question, index) => (
                  <div key={question.id} className="preface-question-row">
                    <select
                      className="input preface-question-type"
                      value={question.type}
                      onChange={(e) => updatePrefaceQuestion(index, { type: e.target.value as PrefaceQuestion['type'] })}
                    >
                      <option value="text">填空题</option>
                      <option value="longtext">开放题</option>
                    </select>
                    <input
                      className="input"
                      value={question.title}
                      onChange={(e) => updatePrefaceQuestion(index, { title: e.target.value })}
                      placeholder="请输入题目标题"
                    />
                    <label className="checkbox preface-required"><input type="checkbox" checked={question.required} onChange={(e) => updatePrefaceQuestion(index, { required: e.target.checked })} /> 必填</label>
                    <button type="button" className="icon-btn icon-danger" onClick={() => removePrefaceQuestion(index)} title="删除前置题">✕</button>
                  </div>
                ))}
                <div className="preface-add-actions">
                  <button type="button" className="btn" onClick={() => addPrefaceQuestion('text')}>＋填空题</button>
                  <button type="button" className="btn" onClick={() => addPrefaceQuestion('longtext')}>＋开放题</button>
                </div>
                <div className="hint">填写者必须勾选"我已阅读"后，才能进入问卷正文。</div>
              </div>
            )}
          </div>

          {/* 电子签 */}
          <div className="drawer-section">
            <div className="drawer-section-label">电子签</div>

            <SettingRow label="启用电子签">
              <Switch
                checked={!!s.signature?.enabled}
                onChange={(v) =>
                  update({ signature: { ...(s.signature ?? {}), enabled: v } })
                }
              />
            </SettingRow>

            {s.signature?.enabled && (
              <div className="drawer-sub-fields">
                <div className="setting-row" style={{ padding: '0' }}>
                  <div className="setting-label">签名模式</div>
                  <div className="setting-control" style={{ minWidth: 120 }}>
                    <div className="select">
                      <select
                        value={s.signature?.mode ?? 'draw'}
                        onChange={(e) =>
                          update({
                            signature: {
                              ...(s.signature ?? {}),
                              mode: e.target.value as 'draw' | 'upload',
                            },
                          })
                        }
                      >
                        <option value="draw">手写</option>
                        <option value="upload">上传图片</option>
                      </select>
                    </div>
                  </div>
                </div>
                <SettingRow label="必签" >
                  <Switch
                    checked={!!s.signature?.required}
                    onChange={(v) =>
                      update({ signature: { ...(s.signature ?? {}), required: v } })
                    }
                  />
                </SettingRow>
                <SettingRow label="签名放在前置页">
                  <Switch
                    checked={!!(s.signature as any)?.onPreface}
                    onChange={(v) =>
                      update({ signature: { ...(s.signature ?? {}), onPreface: v } } as any)
                    }
                  />
                </SettingRow>
                {Boolean((s.signature as any)?.onPreface) && (
                  <div className="hint" style={{ padding: '0 4px' }}>
                    签名板显示在前置页（协议/须知页），与标记了「前置页」的题目一起填写——适合"阅读协议 → 填基本信息 → 签字 → 再答题"的流程。
                  </div>
                )}
                <SettingRow label="生成签署文档（PDF 存档）">
                  <Switch
                    checked={!!(s.document as any)?.enabled}
                    onChange={(v) =>
                      update({ document: { ...((s.document as any) ?? {}), enabled: v } } as any)
                    }
                  />
                </SettingRow>
                {(s.document as any)?.enabled && (
                  <>
                    <input
                      className="input"
                      value={(s.document as any)?.nameQuestion ?? ''}
                      onChange={(e) =>
                        update({ document: { ...((s.document as any) ?? {}), nameQuestion: e.target.value } } as any)
                      }
                      placeholder="姓名题号（用于归档文件名，如 Q1；留空自动识别含「姓名」的题）"
                    />
                    <div className="hint">
                      提交后自动把「答卷 + 签收前置的协议全文 + 手写签名 + 签署日期」渲染成 PDF 存档在服务器。
                      多维表格用推送字段 document_url 放原件链接（点开可查看/下载）；后台「数据记录」可按问卷批量打包下载。
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* 专属链接改答 */}
          <div className="drawer-section">
            <div className="drawer-section-label">改答</div>

            <SettingRow label="启用专属链接改答">
              <Switch
                checked={!!s.editAnswer?.enabled}
                onChange={(v) =>
                  update({ editAnswer: { ...(s.editAnswer ?? {}), enabled: v } })
                }
              />
            </SettingRow>

            {s.editAnswer?.enabled && (
              <div className="drawer-sub-fields">
                <div className="qe-field">
                  <label className="qe-label">token 有效期（小时）</label>
                  <input
                    type="number"
                    className="input"
                    min={1}
                    value={s.editAnswer?.tokenTtlHours ?? 168}
                    onChange={(e) =>
                      update({
                        editAnswer: {
                          ...(s.editAnswer ?? {}),
                          tokenTtlHours: Math.max(1, Number(e.target.value)),
                        },
                      })
                    }
                  />
                </div>
                <div className="hint">默认 168 小时（7 天），改答链接在 done 页展示给用户。</div>
              </div>
            )}
          </div>

          {/* 填写有效期 */}
          <div className="drawer-section">
            <div className="drawer-section-label">填写有效期</div>

            <SettingRow label="启用时间窗">
              <Switch
                checked={!!s.timeLimit?.enabled}
                onChange={(v) => update({ timeLimit: { ...(s.timeLimit ?? {}), enabled: v } })}
              />
            </SettingRow>

            {s.timeLimit?.enabled && (
              <div className="drawer-sub-fields">
                <div className="qe-field">
                  <label className="qe-label">开始时间</label>
                  <input
                    type="datetime-local"
                    className="input"
                    value={isoToLocalInput(s.timeLimit?.start)}
                    onChange={(e) =>
                      update({
                        timeLimit: { ...(s.timeLimit ?? {}), start: localInputToISO(e.target.value) },
                      })
                    }
                  />
                </div>
                <div className="qe-field">
                  <label className="qe-label">结束时间</label>
                  <input
                    type="datetime-local"
                    className="input"
                    value={isoToLocalInput(s.timeLimit?.end)}
                    onChange={(e) =>
                      update({
                        timeLimit: { ...(s.timeLimit ?? {}), end: localInputToISO(e.target.value) },
                      })
                    }
                  />
                </div>
              </div>
            )}
          </div>

          {/* 提交次数限制 */}
          <div className="drawer-section">
            <div className="drawer-section-label">提交次数</div>

            <SettingRow label="限制每位用户提交次数">
              <Switch
                checked={!!s.submitLimitEnabled}
                onChange={(v) => update({ submitLimitEnabled: v })}
              />
            </SettingRow>

            {Boolean(s.submitLimitEnabled) && (
              <div className="drawer-sub-fields">
                <div className="qe-field">
                  <label className="qe-label">最多提交次数</label>
                  <input
                    type="number"
                    className="input"
                    min={1}
                    value={s.submitLimit ?? 1}
                    onChange={(e) =>
                      update({ submitLimit: Math.max(1, Number(e.target.value)) })
                    }
                  />
                </div>
              </div>
            )}
          </div>

          {/* 提交总数配额 */}
          <div className="drawer-section">
            <div className="drawer-section-label">提交总数</div>

            <SettingRow label="限制问卷提交总数">
              <Switch
                checked={!!s.quotaEnabled}
                onChange={(v) => update({ quotaEnabled: v })}
              />
            </SettingRow>

            {Boolean(s.quotaEnabled) && (
              <div className="drawer-sub-fields">
                <div className="qe-field">
                  <label className="qe-label">总数配额</label>
                  <input
                    type="number"
                    className="input"
                    min={1}
                    value={s.quota ?? 100}
                    onChange={(e) => update({ quota: Math.max(1, Number(e.target.value)) })}
                  />
                </div>
              </div>
            )}
          </div>

          {/* 飞书 Webhook 同步（单向 DB → 飞书自动化） */}
          <div className="drawer-section">
            <div className="drawer-section-label">飞书同步</div>

            <div className="drawer-sub-fields" style={{ marginBottom: 8 }}>
              <div className="qe-label">直连多维表格（推荐）</div>
              {(s.feishuTable as any)?.url ? (
                <div className="hint">
                  已关联：<a href={(s.feishuTable as any).url} target="_blank" rel="noreferrer">打开多维表格 ↗</a>
                  ——每道题一个字段（矩阵按行拆列），提交/改答自动写入与更新。
                </div>
              ) : (
                <button type="button" className="btn" onClick={handleCreateTable} disabled={tableBusy} style={{ alignSelf: 'flex-start' }}>
                  {tableBusy ? '创建中…' : '一键创建关联多维表格'}
                </button>
              )}
              {tableMsg && <div className="hint">{tableMsg}</div>}
              <div className="hint">
                自动在飞书建表（服务器配置了知识库时建到指定知识库），无需手动配自动化；与下方 Webhook 方式可并存。
              </div>
            </div>

            <SettingRow label="启用 Webhook 同步">
              <Switch
                checked={!!(s.feishu as any)?.enabled}
                onChange={(v) =>
                  update({ feishu: { ...((s.feishu as any) ?? {}), enabled: v } })
                }
              />
            </SettingRow>

            {(s.feishu as any)?.enabled && (
              <div className="drawer-sub-fields">
                <div className="qe-field">
                  <label className="qe-label">飞书自动化 Webhook URL</label>
                  <input
                    className="input"
                    type="url"
                    placeholder="https://www.feishu.cn/flow/webhook/..."
                    value={(s.feishu as any)?.webhookUrl ?? ''}
                    onChange={(e) =>
                      update({
                        feishu: {
                          ...((s.feishu as any) ?? {}),
                          webhookUrl: e.target.value,
                        },
                      })
                    }
                  />
                </div>
                <div className="qe-field">
                  <label className="qe-label">Bearer Token（飞书凭据校验开启时必填）</label>
                  <input
                    className="input"
                    type="password"
                    placeholder='从飞书自动化"凭据校验"行复制'
                    value={(s.feishu as any)?.bearerToken ?? ''}
                    onChange={(e) =>
                      update({
                        feishu: {
                          ...((s.feishu as any) ?? {}),
                          bearerToken: e.target.value,
                        },
                      })
                    }
                  />
                </div>
                <div className="qe-field">
                  <label className="qe-label">公开访问 Base URL（用于生成签名链接，可选）</label>
                  <input
                    className="input"
                    type="url"
                    placeholder="https://your-domain.com（留空用本站地址）"
                    value={(s.feishu as any)?.publicBaseUrl ?? ''}
                    onChange={(e) =>
                      update({
                        feishu: {
                          ...((s.feishu as any) ?? {}),
                          publicBaseUrl: e.target.value,
                        },
                      })
                    }
                  />
                </div>
                <button
                  type="button"
                  className="btn"
                  onClick={handleFeishuTest}
                  disabled={feishuTesting || !(s.feishu as any)?.webhookUrl}
                  style={{ alignSelf: 'flex-start' }}
                >
                  {feishuTesting ? '测试中…' : '测试连接'}
                </button>
                {feishuMsg && (
                  <div
                    className="hint"
                    style={{
                      padding: '8px 12px',
                      background: feishuMsg.startsWith('✓')
                        ? 'var(--accent-soft-2)'
                        : '#fef2f2',
                      border: `1px solid ${feishuMsg.startsWith('✓') ? 'var(--accent)' : 'var(--danger)'}`,
                      borderRadius: 6,
                      color: feishuMsg.startsWith('✓') ? 'var(--accent-strong)' : 'var(--danger)',
                    }}
                  >
                    {feishuMsg}
                  </div>
                )}
                <div className="hint">
                  推送字段（demo 模板通用）：survey_title / survey_id / event（create·update·test）/
                  submitted_at(_ms) / updated_at(_ms) / q1~q10（按题序）/ answers_text（汇总）/
                  answers（按题号）/ signature_url / document_url（签署文档 PDF）/ edit_token（改答查重键）。
                  签名链接：&lt;base_url&gt;/api/submissions/&lt;edit_token&gt;/signature
                </div>
              </div>
            )}
          </div>

          {/* 提交结果 */}
          <div className="drawer-section">
            <div className="drawer-section-label">提交结果</div>
            <div className="drawer-sub-fields" style={{ padding: '0 12px', gap: 8 }}>
              <input
                className="input"
                value={s.resultPage?.title ?? ''}
                onChange={(e) =>
                  update({ resultPage: { ...(s.resultPage ?? {}), title: e.target.value } })
                }
                placeholder="结果页标题（默认：提交成功 ✓）"
              />
              <textarea
                className="textarea"
                rows={3}
                value={s.resultPage?.body ?? ''}
                onChange={(e) =>
                  update({ resultPage: { ...(s.resultPage ?? {}), body: e.target.value } })
                }
                placeholder="结果页正文（感谢语）"
              />
            </div>

            <SettingRow label="提交后跳转链接">
              <Switch
                checked={!!s.redirectUrl?.enabled}
                onChange={(v) =>
                  update({ redirectUrl: { ...(s.redirectUrl ?? {}), enabled: v } })
                }
              />
            </SettingRow>

            {s.redirectUrl?.enabled && (
              <div className="drawer-sub-fields">
                <input
                  className="input"
                  value={s.redirectUrl?.url ?? ''}
                  onChange={(e) =>
                    update({ redirectUrl: { ...(s.redirectUrl ?? {}), url: e.target.value } })
                  }
                  placeholder="https://example.com/thanks"
                />
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}

/* ===== 子组件 ===== */

function SettingRow({
  label,
  children,
}: {
  label: string;
  help?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="setting-row">
      <div className="setting-label">
        <span>{label}</span>
      </div>
      <div className="setting-control">{children}</div>
    </div>
  );
}

function Switch({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className="switch">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="track" />
    </label>
  );
}

function IconGear() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.01a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h.01a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.01a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function IconX() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

/** ISO 8601 → datetime-local 输入值 */
function isoToLocalInput(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** datetime-local 输入值 → ISO 8601 */
function localInputToISO(local: string): string | undefined {
  if (!local) return undefined;
  const d = new Date(local);
  if (isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

/** 前置页配图（≤1.5MB，data URL 存入 settings.preface.image） */
function PrefaceImageField({ image, onChange }: { image?: string; onChange: (image?: string) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="preface-image-field">
      <div className="qe-label">前置页图片</div>
      {image ? (
        <div className="preface-image-preview">
          <img src={image} alt="前置页图片" />
          <button type="button" className="btn" onClick={() => onChange(undefined)}>删除图片</button>
        </div>
      ) : (
        <button type="button" className="btn" onClick={() => inputRef.current?.click()}>＋上传图片</button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          if (file.size > 1.5 * 1024 * 1024) { alert('图片不能超过 1.5MB'); return; }
          const reader = new FileReader();
          reader.onload = () => typeof reader.result === 'string' && onChange(reader.result);
          reader.readAsDataURL(file);
          e.target.value = '';
        }}
      />
    </div>
  );
}